import type { ActivePopover } from '../context';

const TEXT_NAV_PARTS = new Set(['key', 'op', 'value', 'operator', 'type']);

/**
 * Returns all focusable elements within `container` in DOM order.
 * Skips elements that are hidden, disabled, aria-hidden, or have tabIndex < 0.
 */
export function getFocusableCells(container: HTMLElement): HTMLElement[] {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      const el = node as HTMLElement;
      if (el.getAttribute('aria-hidden') === 'true') {
        return NodeFilter.FILTER_REJECT;
      }
      if (el.hidden || (el as HTMLInputElement).disabled) {
        return NodeFilter.FILTER_SKIP;
      }
      if (el.tabIndex >= 0) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });
  const cells: HTMLElement[] = [];
  let node = walker.nextNode();
  while (node) {
    cells.push(node as HTMLElement);
    node = walker.nextNode();
  }
  return cells;
}

export function isTextNavCell(el: HTMLElement): boolean {
  if (el.hasAttribute('data-freetext-token')) {
    return true;
  }
  const part = el.getAttribute('data-part');
  if (part && TEXT_NAV_PARTS.has(part)) {
    return true;
  }
  return el instanceof HTMLInputElement && !part;
}

export function getTextNavCells(container: HTMLElement): HTMLElement[] {
  return getFocusableCells(container).filter(isTextNavCell);
}

export function moveCaretByWord(input: HTMLInputElement, direction: 'left' | 'right'): void {
  const value = input.value;
  let pos = input.selectionStart ?? 0;
  if (direction === 'left') {
    while (pos > 0 && /\s/.test(value[pos - 1] ?? '')) {
      pos -= 1;
    }
    while (pos > 0 && !/\s/.test(value[pos - 1] ?? '')) {
      pos -= 1;
    }
  } else {
    while (pos < value.length && !/\s/.test(value[pos] ?? '')) {
      pos += 1;
    }
    while (pos < value.length && /\s/.test(value[pos] ?? '')) {
      pos += 1;
    }
  }
  input.setSelectionRange(pos, pos);
}

export type TextEditFocusOptions = {
  caret: 'start' | 'end';
  setActivePopover?: (popover: ActivePopover) => void;
  word?: 'left' | 'right';
  draft?: string;
};

const ACTIVATABLE_PARTS = new Set(['key', 'op', 'value', 'operator']);

function isActivatablePart(part: string | null): part is 'key' | 'op' | 'value' | 'operator' {
  return part !== null && ACTIVATABLE_PARTS.has(part);
}

export function focusForTextEditing(el: HTMLElement, options: TextEditFocusOptions): void {
  const { caret, setActivePopover, word, draft } = options;
  const part = el.getAttribute('data-part');
  const tokenKey =
    el.closest<HTMLElement>('[data-token-container][data-key]')?.getAttribute('data-key') ??
    el.getAttribute('data-key');

  if (setActivePopover && tokenKey && isActivatablePart(part)) {
    setActivePopover(draft !== undefined ? { tokenKey, part, draft } : { tokenKey, part });
    requestAnimationFrame(() => {
      const container = document.querySelector<HTMLElement>(
        `[data-token-container][data-key="${CSS.escape(tokenKey)}"]`,
      );
      const input = container?.querySelector<HTMLInputElement>(`input[data-part="${CSS.escape(part)}"]`);
      if (input) {
        input.focus();
        const pos = caret === 'start' ? 0 : input.value.length;
        input.setSelectionRange(pos, pos);
        if (word) {
          moveCaretByWord(input, word);
        }
        return;
      }
      container?.querySelector<HTMLElement>(`[data-part="${CSS.escape(part)}"]`)?.focus();
    });
    return;
  }

  el.focus();
  if (el instanceof HTMLInputElement) {
    const pos = caret === 'start' ? 0 : el.value.length;
    el.setSelectionRange(pos, pos);
    if (word) {
      moveCaretByWord(el, word);
    }
  }
}

export function findAdjacentTextNavCell(
  root: HTMLElement,
  fromEl: HTMLElement,
  direction: 'left' | 'right',
): HTMLElement | null {
  const cells = getTextNavCells(root);
  const following = Node.DOCUMENT_POSITION_FOLLOWING;
  const preceding = Node.DOCUMENT_POSITION_PRECEDING;
  const candidates = cells.filter((cell) => {
    if (cell === fromEl || fromEl.contains(cell) || cell.contains(fromEl)) {
      return false;
    }
    const pos = fromEl.compareDocumentPosition(cell);
    // oxlint-disable-next-line no-bitwise
    return direction === 'left' ? !!(pos & preceding) : !!(pos & following);
  });
  return direction === 'left' ? (candidates[candidates.length - 1] ?? null) : (candidates[0] ?? null);
}

export function focusQueryEdge(
  root: HTMLElement,
  direction: 'left' | 'right',
  setActivePopover?: (popover: ActivePopover) => void,
): boolean {
  const cells = getTextNavCells(root);
  if (!cells.length) {
    return false;
  }
  const target = direction === 'left' ? cells[0] : cells[cells.length - 1];
  focusForTextEditing(target, {
    caret: direction === 'left' ? 'start' : 'end',
    setActivePopover,
  });
  return true;
}

/** @deprecated Prefer findAdjacentTextNavCell + focusForTextEditing. */
export function focusAdjacentGap(root: HTMLElement, fromEl: HTMLElement | null, direction: 'left' | 'right'): boolean {
  if (!fromEl) {
    return false;
  }
  const gaps = Array.from(root.querySelectorAll<HTMLElement>('[data-freetext-token]')).filter((g) => g !== fromEl);
  const wanted = direction === 'left' ? Node.DOCUMENT_POSITION_FOLLOWING : Node.DOCUMENT_POSITION_PRECEDING;
  // oxlint-disable-next-line no-bitwise
  const candidates = gaps.filter((g) => g.compareDocumentPosition(fromEl) & wanted);
  const target = direction === 'left' ? candidates[candidates.length - 1] : candidates[0];
  if (!target) {
    return false;
  }
  target.focus();
  if (target instanceof HTMLInputElement) {
    const pos = direction === 'left' ? target.value.length : 0;
    target.setSelectionRange(pos, pos);
  }
  return true;
}
